import {FC, FormEvent, ReactNode} from 'react'
import {useTranslation} from 'react-i18next'
import {useHistory} from 'react-router-dom'

import {
  ArrowForwardSharp,
  KeyboardArrowLeftSharp,
  KeyboardArrowRightSharp,
} from '@mui/icons-material'
import {Box, MobileStepper, Tooltip} from '@mui/material'
import {Theme} from '@mui/material/styles'
import {PGButton} from '@progynyglobal/pg-ui-library'
import {makeStyles} from 'tss-react/mui'

import {useResponsive} from '@progyny/hooks/useResponsive'

type EventHandlerType = (event: FormEvent) => void

type StepperProps = {
  isDisabled?: boolean
  activeStep: number
  stepNumber?: number
  toContinuePath: EventHandlerType | string
  toCancelPath?: EventHandlerType | string
  cancelButtonText?: string
  continueButtonText?: string
  isContinueLoading?: boolean
  continueButtonDisabled?: boolean
  continueButtonTooltip?: ReactNode
  continueButtonTooltipClassName?: string
  cancelButtonAriaLabel?: string
  continueButtonAriaLabel?: string
  desktopMarginTop?: number
  desktopJustifyContent?: 'center' | 'end'
  showBackButton?: boolean
}

const useStyles = makeStyles<{desktopMarginTop: number}>()(
  (theme: Theme, {desktopMarginTop}) => ({
    root: {
      zIndex: 2,
      flexGrow: 1,
      margin: '32px',
    },
    desktopRow: {
      display: 'flex',
      marginTop: theme.spacing(desktopMarginTop),
      flexDirection: 'row',
      alignItems: 'center',
    },
  })
)

export const ReimbursementMobileStepper: FC<StepperProps> = (props) => {
  const {t} = useTranslation()
  const {
    isDisabled,
    activeStep,
    stepNumber = 4,
    toContinuePath,
    toCancelPath,
    cancelButtonText = t('common_cancel'),
    continueButtonText,
    isContinueLoading,
    continueButtonDisabled,
    continueButtonTooltip,
    continueButtonTooltipClassName,
    cancelButtonAriaLabel,
    continueButtonAriaLabel,
    desktopMarginTop = 4,
    desktopJustifyContent = 'center',
    showBackButton = true,
  } = props
  const {classes} = useStyles({desktopMarginTop})
  const history = useHistory()
  const isDownSM = useResponsive('down', 'sm')

  const isFunction =
    typeof toCancelPath === 'function' || typeof toContinuePath === 'function'

  function goToPath(toPath: any) {
    if (typeof toPath === 'function') {
      toPath()
    } else {
      return history.push(toPath)
    }
  }

  const nextLabel =
    continueButtonText ?? (isFunction ? t('common_submit') : t('common_next'))
  const nextDisabled = continueButtonDisabled ?? isDisabled

  if (isDownSM) {
    return (
      <MobileStepper
        variant={'dots'}
        steps={stepNumber}
        activeStep={activeStep}
        classes={{root: classes.root}}
        backButton={
          showBackButton && (
            <PGButton
              onClick={() => goToPath(toCancelPath)}
              label={cancelButtonText}
              startIcon={<KeyboardArrowLeftSharp />}
              variant='outlined'
              aria-label={cancelButtonAriaLabel}
            />
          )
        }
        nextButton={
          <PGButton
            onClick={() => goToPath(toContinuePath)}
            disabled={nextDisabled}
            loading={isContinueLoading}
            label={nextLabel}
            endIcon={<KeyboardArrowRightSharp />}
            aria-label={continueButtonAriaLabel}
          />
        }
      />
    )
  }

  const nextButton = (
    <PGButton
      onClick={() => goToPath(toContinuePath)}
      disabled={nextDisabled}
      loading={isContinueLoading}
      label={nextLabel}
      endIcon={<ArrowForwardSharp />}
      aria-label={continueButtonAriaLabel}
    />
  )

  return (
    <Box className={classes.desktopRow} justifyContent={desktopJustifyContent}>
      {showBackButton && (
        <Box display={'flex'} justifyContent={'center'} mr={2}>
          <PGButton
            onClick={() => goToPath(toCancelPath)}
            variant='outlined'
            label={cancelButtonText}
            aria-label={cancelButtonAriaLabel}
          />
        </Box>
      )}
      <Box display={'flex'} justifyContent={'center'}>
        {continueButtonTooltip ? (
          <Tooltip
            title={continueButtonTooltip}
            placement='top'
            classes={{tooltip: continueButtonTooltipClassName}}
          >
            <Box>{nextButton}</Box>
          </Tooltip>
        ) : (
          nextButton
        )}
      </Box>
    </Box>
  )
}
